import { useMemo } from 'react';
import { AlertTriangle, ChevronRight, Clock3, Inbox, MapPin, UserRound } from 'lucide-react';
import type { TraceListItem } from '../types';
import { formatDuration, formatNumber, formatTime, sourceLabel } from '../lib/format';
import { StatusPill } from './StatusPill';

interface TraceListProps {
  traces: readonly TraceListItem[];
  total: number;
  selectedId: string | undefined;
  busy: boolean;
  onSelect: (trace: TraceListItem) => void;
}

export function TraceList({ traces, total, selectedId, busy, onSelect }: TraceListProps) {
  const roomGroups = useMemo(() => {
    const groups = new Map<string, TraceListItem[]>();
    traces.forEach((trace) => groups.set(trace.roomId, [...(groups.get(trace.roomId) ?? []), trace]));
    return [...groups.entries()];
  }, [traces]);
  return (
    <section className="trace-list panel" aria-label="最近 Trace">
      <header className="panel__header trace-list__header">
        <div>
          <span className="eyebrow">RECENT TRACES</span>
          <h2>最近运行</h2>
        </div>
        <span className="count-badge">{total}</span>
      </header>
      {busy ? (
        <div className="trace-list__skeleton" aria-label="正在加载 Trace">
          {[1, 2, 3, 4].map((item) => <span key={item} />)}
        </div>
      ) : traces.length === 0 ? (
        <div className="empty-state">
          <Inbox size={24} />
          <strong>没有匹配的 Trace</strong>
          <p>调整筛选条件，或等待新的运行进入采集管线。</p>
        </div>
      ) : (
        <div className="trace-list__items">
          {roomGroups.map(([roomId, roomTraces]) => <section className="room-group" aria-label={`Room ${roomId}`} key={roomId}>
            <header className="room-group__header"><span><MapPin size={11} />{roomId}</span><b>{roomTraces.length}</b></header>
            {roomTraces.map((trace) => (
            <button
              className={`trace-row${selectedId === trace.traceId ? ' trace-row--selected' : ''}`}
              key={trace.traceId}
              onClick={() => onSelect(trace)}
              aria-pressed={selectedId === trace.traceId}
            >
              <span className={`trace-row__rail trace-row__rail--${trace.status}`} aria-hidden="true" />
              <span className="trace-row__body">
                <span className="trace-row__topline">
                  <strong>{trace.headline}</strong>
                  <StatusPill status={trace.status} compact />
                </span>
                <span className="trace-row__ids">
                  <code>{trace.runId}</code>
                  <span>·</span>
                  <code>{trace.roomId}</code>
                </span>
                <span className="trace-row__meta">
                  <span><Clock3 size={13} />{formatTime(trace.startedAt)}</span>
                  <span>{sourceLabel(trace.source)}</span>
                  {trace.playerId && <span><UserRound size={13} />{trace.playerId.replace('player_', '')}</span>}
                </span>
                <span className="trace-row__metrics">
                  <span>耗时 <b>{formatDuration(trace.durationMs)}</b></span>
                  <span>Tokens <b>{formatNumber(trace.tokenUsage?.total)}</b></span>
                  {trace.error && <span className="trace-row__error"><AlertTriangle size={13} />时序异常</span>}
                </span>
              </span>
              <ChevronRight className="trace-row__chevron" size={17} aria-hidden="true" />
            </button>
            ))}
          </section>)}
        </div>
      )}
    </section>
  );
}
